// The Refer a Company form's ten fields, its consent box and their checks (MOTHER-88): the
// same rules as REFERRAL_RULES, TIMELINE_OPTIONS and the consent rule in
// apps/server/src/partners.mjs, which the partner bundle may not import
// (partnerBundle.test.ts). referral.test.ts holds the two to the same labels, limits,
// options and messages, so a change on one side fails the build.

export const TIMELINE_OPTIONS = ['Ready now', 'Next 3 months', '3–6 months', 'Just exploring'] as const;

export type ReferralKey =
  | 'firstName'
  | 'lastName'
  | 'email'
  | 'phone'
  | 'title'
  | 'companyName'
  | 'companyWebsite'
  | 'relationship'
  | 'need'
  | 'timeline';

export interface ReferralRule {
  key: ReferralKey;
  label: string;
  required: boolean;
  max: number;
  email?: boolean;
  options?: readonly string[];
}

/** In the order the form shows them, which is the API's order. */
export const REFERRAL_FIELDS: readonly ReferralRule[] = [
  { key: 'firstName', label: 'First Name', required: true, max: 100 },
  { key: 'lastName', label: 'Last Name', required: true, max: 100 },
  { key: 'email', label: 'Email', required: true, max: 300, email: true },
  { key: 'phone', label: 'Phone', required: false, max: 200 },
  { key: 'title', label: 'Title', required: false, max: 300 },
  { key: 'companyName', label: 'Company', required: true, max: 200 },
  { key: 'companyWebsite', label: 'Website', required: false, max: 300 },
  { key: 'relationship', label: 'How You Know Them', required: false, max: 2000 },
  { key: 'need', label: 'What They Are Looking For', required: true, max: 2000 },
  { key: 'timeline', label: 'Timeline', required: false, max: 300, options: TIMELINE_OPTIONS },
];

/** The checkbox below the fields: the API refuses a referral without `consent: true`. */
export const CONSENT_LABEL = 'They Know I Am Introducing Them';
/** The API's consent message, word for word. */
export const CONSENT_MESSAGE = 'Confirm they know you are introducing them.';

/** What the partner has typed, one string per field ('' when left empty), and the consent box. */
export type ReferralValues = Record<ReferralKey, string> & { consent: boolean };

/** One message per failing field, keyed as the API keys them (`consent` for the checkbox). */
export type ReferralErrors = Partial<Record<string, string>>;

/** The POST /api/partner/referrals body: the ten keys, trimmed, and `consent: true`. */
export type ReferralBody = Record<ReferralKey, string> & { consent: true };

// The API's email shape (partners.mjs EMAIL).
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function emptyReferral(): ReferralValues {
  return {
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    title: '',
    companyName: '',
    companyWebsite: '',
    relationship: '',
    need: '',
    timeline: '',
    consent: false,
  };
}

/**
 * The API's checks, in its order: required, then length (after trimming), then the email
 * shape, then the option list, then the consent box, with its messages word for word. An
 * empty object means the form may be sent.
 */
export function validateReferral(values: ReferralValues): ReferralErrors {
  const errors: ReferralErrors = {};
  for (const rule of REFERRAL_FIELDS) {
    const text = values[rule.key].trim();
    if (text === '') {
      if (rule.required) errors[rule.key] = `${rule.label} is required.`;
    } else if (text.length > rule.max) {
      errors[rule.key] = `${rule.label} must be ${rule.max.toLocaleString('en-US')} characters or fewer.`;
    } else if (rule.email && !EMAIL.test(text)) {
      errors[rule.key] = `${rule.label} must be an email address.`;
    } else if (rule.options && !rule.options.includes(text)) {
      errors[rule.key] = `${rule.label} must be one of the listed options.`;
    }
  }
  if (values.consent !== true) errors.consent = CONSENT_MESSAGE;
  return errors;
}

/**
 * The POST body: exactly the ten keys, trimmed, and `consent: true`. Never a stage, a
 * status or a partner account (the server takes the partner from the session). Only
 * called once `validateReferral` passed, so the consent box is ticked.
 */
export function referralBody(values: ReferralValues): ReferralBody {
  const fields = {} as Record<ReferralKey, string>;
  for (const rule of REFERRAL_FIELDS) fields[rule.key] = values[rule.key].trim();
  return { ...fields, consent: true };
}
